<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col cols="12">
        <b-card no-body class="modern-card">
          <div class="modern-card-header">
            <h4 class="header-title">Questionários</h4>
            <div class="header-actions">
              <b-button
                v-if="mainTabIndex === 0 && canCreateQuestionnaire"
                variant="primary"
                class="d-flex align-items-center btn-new"
                @click="createQuestionnaire"
              >
                <i class="fa fa-plus mr-2"></i> Novo Questionário
              </b-button>
              <b-button
                v-if="canCreateQuestionnaire"
                variant="outline-primary"
                class="d-flex align-items-center btn-new btn-new-dashboard"
                @click="openCreateDashboardModal"
              >
                <i class="fa fa-chart-pie mr-2"></i> Novo Dashboard
              </b-button>
            </div>
          </div>

          <b-card-body class="p-4">
            <b-tabs v-model="mainTabIndex" content-class="pt-4" fill>
              <b-tab title="Questionários">
                <b-row class="mb-4">
                  <b-col md="4">
                    <label class="text-sub font-weight-bold mb-2"
                      >Filtrar por status</label
                    >
                    <b-form-select
                      v-model="filter.active"
                      @change="loadQuestionnaires"
                      class="custom-select-modern"
                    >
                      <option :value="null">Todos</option>
                      <option :value="true">Ativos</option>
                      <option :value="false">Inativos</option>
                    </b-form-select>
                  </b-col>
                </b-row>

                <div v-if="loading" class="text-center py-5">
                  <b-spinner variant="primary"></b-spinner>
                  <p class="mt-3 text-sub">Carregando questionários...</p>
                </div>

                <div v-else-if="questionnaires.length > 0">
                  <b-table
                    :items="questionnaires"
                    :fields="fields"
                    responsive
                    class="modern-table"
                    borderless
                    hover
                    @row-clicked="onRowClicked"
                    tbody-tr-class="clickable-row"
                  >
                    <template #cell(title)="data">
                      <div class="title-text">{{ data.item.title }}</div>
                      <div class="text-sub mt-1">
                        {{ data.item.description || "Sem descrição" }}
                      </div>
                    </template>

                    <template #cell(active)="data">
                      <span
                        :class="['status-badge', statusBadgeClass(data.item)]"
                      >
                        {{ statusLabel(data.item) }}
                      </span>
                    </template>

                    <template #cell(stats)="data">
                      <div
                        v-if="data.item.participationStats"
                        class="stats-container participation-summary"
                      >
                        <div
                          class="participation-line participation-line--primary"
                        >
                          <span class="participation-label">Email:</span>
                          <strong class="participation-strong">
                            {{
                              data.item.participationStats.channels.email
                                .responded
                            }}/
                            {{
                              data.item.participationStats.channels.email.sent
                            }}
                          </strong>
                          <span class="participation-muted">enviado</span>
                        </div>

                        <div class="participation-chip-row">
                          <span class="participation-chip">
                            {{ data.item.participationStats.channels.web }} web
                          </span>
                          <span class="participation-chip">
                            {{ data.item.participationStats.channels.qrcode }}
                            qrcode
                          </span>
                        </div>
                      </div>
                      <span v-else class="text-sub">Sem dados</span>
                    </template>

                    <template #cell(dates)="data">
                      <div class="d-flex flex-column dates-container">
                        <span v-if="data.item.startDate" class="text-sub mb-1">
                          <i class="far fa-calendar-alt mr-1"></i>
                          {{ formatDate(data.item.startDate) }}
                        </span>
                        <span
                          v-if="data.item.endDate"
                          class="text-sub text-muted"
                        >
                          <i class="far fa-calendar-check mr-1"></i>
                          {{ formatDate(data.item.endDate) }}
                        </span>
                      </div>
                    </template>

                    <template #cell(actions)="data">
                      <div
                        class="d-flex justify-content-end align-items-center"
                      >
                        <button
                          class="action-btn action-btn-info"
                          @click.stop="viewAnalytics(data.item)"
                          v-b-tooltip.hover
                          title="Ver Analytics"
                          :disabled="isDraftQuestionnaire(data.item)"
                        >
                          <i class="fa fa-chart-bar"></i>
                        </button>
                        <button
                          class="action-btn"
                          style="background-color: #f3e8ff; color: #9333ea"
                          @click.stop="openAiAnalysis(data.item)"
                          v-b-tooltip.hover
                          title="Análise IA"
                          :disabled="isDraftQuestionnaire(data.item)"
                        >
                          <i class="fas fa-robot"></i>
                        </button>
                        <button
                          class="action-btn action-btn-secondary"
                          @click.stop="viewObservations(data.item)"
                          v-b-tooltip.hover
                          title="Observações"
                          :disabled="isDraftQuestionnaire(data.item)"
                        >
                          <i class="fa fa-comments"></i>
                        </button>
                        <button
                          v-if="canEditQuestionnaire"
                          class="action-btn action-btn-secondary"
                          @click.stop="manageDistribution(data.item)"
                          v-b-tooltip.hover
                          title="Distribuir"
                          :disabled="isDraftQuestionnaire(data.item)"
                        >
                          <i class="fa fa-paper-plane"></i>
                        </button>
                        <button
                          class="action-btn action-btn-success"
                          @click.stop="generateQRCode(data.item)"
                          v-b-tooltip.hover
                          :title="qrCodeActionTitle(data.item)"
                          :disabled="isQRCodeDisabled(data.item)"
                        >
                          <i class="fa fa-qrcode"></i>
                        </button>
                        <button
                          v-if="canCreateQuestionnaire"
                          class="action-btn action-btn-clone"
                          @click.stop="openCloneModal(data.item)"
                          v-b-tooltip.hover
                          title="Clonar"
                        >
                          <i class="fa fa-clone"></i>
                        </button>
                        <button
                          v-if="canEditQuestionnaire"
                          class="action-btn action-btn-primary"
                          @click.stop="editQuestionnaire(data.item)"
                          v-b-tooltip.hover
                          title="Editar"
                        >
                          <i class="fa fa-edit"></i>
                        </button>
                        <button
                          v-if="canEditQuestionnaire"
                          class="action-btn action-btn-danger"
                          @click.stop="deleteQuestionnaire(data.item)"
                          v-b-tooltip.hover
                          title="Desativar"
                          :disabled="!data.item.active"
                        >
                          <i class="fa fa-trash"></i>
                        </button>
                      </div>
                    </template>
                  </b-table>
                </div>

                <div v-else class="text-center py-5">
                  <div class="mb-4">
                    <i
                      class="fa fa-clipboard-list fa-4x text-light"
                      style="color: #e2e8f0"
                    ></i>
                  </div>
                  <h5 class="text-dark mb-2">Nenhum questionário encontrado</h5>
                  <p class="text-sub mb-4">
                    Crie seu primeiro questionário para começar a coletar
                    respostas.
                  </p>
                  <b-button
                    v-if="canCreateQuestionnaire"
                    variant="primary"
                    class="btn-new"
                    @click="createQuestionnaire"
                  >
                    <i class="fa fa-plus mr-2"></i> Criar Questionário
                  </b-button>
                </div>
              </b-tab>

              <b-tab title="Dashboards">
                <div v-if="dashboardsLoading" class="text-center py-5">
                  <b-spinner variant="primary"></b-spinner>
                  <p class="mt-3 text-sub">Carregando dashboards...</p>
                </div>

                <div v-else-if="dashboards.length > 0">
                  <b-table
                    :items="dashboards"
                    :fields="dashboardFields"
                    responsive
                    class="modern-table"
                    borderless
                    hover
                  >
                    <template #cell(name)="data">
                      <div class="dashboard-table-name">
                        {{ data.item.name }}
                      </div>
                      <div class="text-sub mt-1">
                        {{ dashboardQuestionnaireSummary(data.item) }}
                      </div>
                    </template>

                    <template #cell(actions)="data">
                      <div
                        class="d-flex justify-content-end align-items-center"
                      >
                        <button
                          class="action-btn action-btn-info"
                          @click.stop="viewDashboard(data.item)"
                          v-b-tooltip.hover
                          title="Ver dashboard"
                        >
                          <i class="fa fa-chart-pie"></i>
                        </button>
                        <button
                          v-if="canEditQuestionnaire"
                          class="action-btn action-btn-primary"
                          @click.stop="openEditDashboardModal(data.item)"
                          v-b-tooltip.hover
                          title="Editar"
                        >
                          <i class="fa fa-edit"></i>
                        </button>
                      </div>
                    </template>
                  </b-table>
                </div>

                <div v-else class="text-center py-5">
                  <div class="mb-4">
                    <i
                      class="fa fa-chart-pie fa-4x text-light"
                      style="color: #e2e8f0"
                    ></i>
                  </div>
                  <h5 class="text-dark mb-2">Nenhum dashboard encontrado</h5>
                  <p class="text-sub mb-4">
                    Crie um dashboard para consolidar respostas de múltiplos
                    questionários.
                  </p>
                  <b-button
                    v-if="canCreateQuestionnaire"
                    variant="primary"
                    class="btn-new"
                    @click="openCreateDashboardModal"
                  >
                    <i class="fa fa-chart-pie mr-2"></i> Criar Dashboard
                  </b-button>
                </div>
              </b-tab>
            </b-tabs>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-modal
      v-model="templateSelector.show"
      title="Selecionar modelo de questionário"
      centered
      size="lg"
      hide-header-close
    >
      <div class="template-grid">
        <button
          v-for="template in templateSelector.options"
          :key="template.key"
          type="button"
          class="template-card"
          :class="{
            'template-card-selected':
              templateSelector.selected === template.key,
            'template-card-disabled': template.disabled,
          }"
          :disabled="template.disabled"
          @click="selectTemplate(template.key)"
        >
          <div class="template-card-title">{{ template.title }}</div>
          <div class="template-card-description">
            {{ template.description }}
          </div>
          <span
            class="status-badge"
            :class="
              template.disabled ? 'badge-soft-secondary' : 'badge-soft-success'
            "
          >
            {{ template.disabled ? "Em desenvolvimento" : "Disponível" }}
          </span>
        </button>
      </div>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="closeTemplateSelector">
          Fechar
        </b-button>
        <b-button
          variant="primary"
          :disabled="!templateSelector.selected"
          @click="continueWithSelectedTemplate"
        >
          Continuar
        </b-button>
      </template>
    </b-modal>

    <b-modal
      v-model="cloneModal.show"
      title="Clonar questionário"
      centered
      :no-close-on-backdrop="cloneModal.loading"
      :no-close-on-esc="cloneModal.loading"
      :hide-header-close="cloneModal.loading"
      @hidden="resetCloneModal"
      @shown="focusCloneTitleInput"
    >
      <div v-if="cloneModal.sourceItem">
        <p class="text-sub mb-3">
          Informe o título do novo questionário baseado em
          <strong>{{ cloneModal.sourceItem.title }}</strong
          >.
        </p>

        <b-form-group
          label="Título do novo questionário"
          label-for="clone-questionnaire-title"
        >
          <b-form-input
            id="clone-questionnaire-title"
            ref="cloneTitleInput"
            v-model.trim="cloneModal.title"
            :state="cloneTitleState"
            :disabled="cloneModal.loading"
            maxlength="160"
            placeholder="Digite o título do clone"
            @keyup.enter="confirmCloneQuestionnaire"
          ></b-form-input>
          <b-form-invalid-feedback :state="cloneTitleState">
            Informe o título do questionário clonado.
          </b-form-invalid-feedback>
        </b-form-group>
      </div>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          :disabled="cloneModal.loading"
          @click="cloneModal.show = false"
        >
          Cancelar
        </b-button>
        <b-button
          variant="primary"
          :disabled="cloneModal.loading"
          @click="confirmCloneQuestionnaire"
        >
          <b-spinner v-if="cloneModal.loading" small class="mr-2"></b-spinner>
          {{ cloneModal.loading ? "Clonando..." : "Clonar" }}
        </b-button>
      </template>
    </b-modal>

    <b-modal
      v-model="dashboardModal.show"
      :title="dashboardModal.id ? 'Editar dashboard' : 'Novo dashboard'"
      centered
      size="lg"
      :no-close-on-backdrop="dashboardModal.loading"
      :no-close-on-esc="dashboardModal.loading"
      :hide-header-close="dashboardModal.loading"
      @hidden="resetDashboardModal"
    >
      <b-form-group label="Nome do dashboard" label-for="dashboard-name">
        <b-form-input
          id="dashboard-name"
          v-model.trim="dashboardModal.name"
          :state="dashboardNameState"
          :disabled="dashboardModal.loading"
          maxlength="160"
          placeholder="Digite o nome do dashboard"
          @keyup.enter="submitDashboard"
        ></b-form-input>
        <b-form-invalid-feedback :state="dashboardNameState">
          Informe o nome do dashboard.
        </b-form-invalid-feedback>
      </b-form-group>

      <div class="dashboard-modal-section">
        <div class="dashboard-modal-title">Questionários disponíveis</div>
        <p class="text-sub mb-3">
          Selecione os questionários que farão parte deste dashboard
          consolidado.
        </p>

        <b-row class="mb-3 align-items-end">
          <b-col md="8">
            <b-form-group class="mb-0" label="Pesquisar questionário">
              <b-input-group>
                <b-input-group-prepend is-text>
                  <i class="fa fa-search"></i>
                </b-input-group-prepend>
                <b-form-input
                  v-model.trim="dashboardQuestionnaireSearch"
                  :disabled="dashboardOptionsLoading || dashboardModal.loading"
                  placeholder="Pesquisar por título ou descrição"
                ></b-form-input>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group class="mb-0" label="Itens por página">
              <b-form-select
                v-model="dashboardQuestionnairePageSize"
                :options="dashboardPageSizeOptions"
                :disabled="dashboardOptionsLoading || dashboardModal.loading"
                class="custom-select-modern"
              ></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <div v-if="dashboardOptionsLoading" class="text-center py-4">
          <b-spinner variant="primary"></b-spinner>
          <p class="text-sub mt-3 mb-0">Carregando questionários...</p>
        </div>

        <div v-else-if="filteredDashboardQuestionnaires.length">
          <div class="complaints-layout-surface">
            <b-table
              :items="paginatedDashboardQuestionnaires"
              :fields="dashboardQuestionnaireFields"
              :sort-by.sync="dashboardQuestionnaireSortBy"
              :sort-desc.sync="dashboardQuestionnaireSortDesc"
              :tbody-tr-class="dashboardQuestionnaireRowClass"
              responsive
              borderless
              hover
              show-empty
              table-class="complaints-layout-table dashboard-picker-table"
              thead-class="complaints-layout-header"
              class="dashboard-picker-table"
            >
              <template #cell(selected)="data">
                <b-form-checkbox
                  :checked="isDashboardQuestionnaireSelected(data.item._id)"
                  :disabled="dashboardModal.loading"
                  @change="toggleDashboardQuestionnaire(data.item._id, $event)"
                ></b-form-checkbox>
              </template>

              <template #cell(title)="data">
                <div class="dashboard-questionnaire-title">
                  {{ data.item.title }}
                </div>
                <div class="text-sub mt-1">
                  {{ data.item.description || "Sem descrição" }}
                </div>
              </template>

              <template #empty>
                <div class="text-muted text-center py-5">
                  <i class="fa fa-search fa-2x"></i>
                  <p class="mt-3 mb-0">Nenhum questionário encontrado</p>
                </div>
              </template>
            </b-table>
          </div>

          <div
            class="d-flex justify-content-between align-items-center flex-wrap mt-3"
          >
            <small class="text-sub mb-2 mb-md-0">
              Exibindo {{ dashboardQuestionnaireRangeStart }}-{{
                dashboardQuestionnaireRangeEnd
              }}
              de {{ filteredDashboardQuestionnaires.length }} questionário(s)
            </small>

            <nav class="pagination-nav dashboard-pagination-nav mb-0">
              <b-pagination
                v-model="dashboardQuestionnairePage"
                :total-rows="filteredDashboardQuestionnaires.length"
                :per-page="dashboardQuestionnairePageSize"
                prev-text="Anterior"
                next-text="Próximo"
                align="center"
                class="mb-0"
              ></b-pagination>
            </nav>
          </div>
        </div>

        <div v-else class="text-center py-4 text-sub">
          Nenhum questionário encontrado para a pesquisa informada.
        </div>

        <small
          v-if="
            dashboardModal.submitted && !dashboardModal.questionnaireIds.length
          "
          class="text-danger d-block mt-3"
        >
          Selecione pelo menos um questionário.
        </small>
      </div>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          :disabled="dashboardModal.loading"
          @click="dashboardModal.show = false"
        >
          Cancelar
        </b-button>
        <b-button
          variant="primary"
          :disabled="dashboardModal.loading || dashboardOptionsLoading"
          @click="submitDashboard"
        >
          <b-spinner
            v-if="dashboardModal.loading"
            small
            class="mr-2"
          ></b-spinner>
          {{ dashboardModal.loading ? "Salvando..." : saveDashboardLabel }}
        </b-button>
      </template>
    </b-modal>

    <!-- Sidebar 75% -->
    <b-sidebar
      v-model="sidebar.show"
      right
      shadow
      width="75%"
      backdrop
      no-header
      body-class="p-0 d-flex flex-column"
    >
      <div
        class="p-3 border-bottom d-flex justify-content-between align-items-center bg-light"
      >
        <h5 class="mb-0">
          {{
            sidebar.isNew
              ? "Novo Questionário"
              : questionnaireTitle(sidebar.questionnaireId)
          }}
        </h5>
        <b-button
          size="sm"
          variant="outline-secondary"
          @click="sidebar.show = false"
        >
          <i class="fa fa-times"></i> Fechar
        </b-button>
      </div>

      <div class="flex-grow-1 p-3" style="overflow-y: auto">
        <b-tabs v-model="sidebar.tabIndex" content-class="mt-3">
          <!-- Formulário -->
          <b-tab title="Questionário" active>
            <NR1QuestionnaireForm
              v-if="sidebar.show && (sidebar.tabIndex === 0 || sidebar.isNew)"
              :questionnaire-id="sidebar.questionnaireId"
              :initial-template-key="sidebar.initialTemplateKey"
              @saved="onQuestionnaireSaved"
              @cancel="sidebar.show = false"
            />
          </b-tab>

          <!-- QR Code -->
          <b-tab title="QR Code" :disabled="sidebar.isNew">
            <div
              v-if="sidebar.show && sidebar.tabIndex === 1 && !sidebar.isNew"
            >
              <div class="text-center" v-if="qrCodeData.loading">
                <b-spinner></b-spinner>
                <p>Gerando QR Code...</p>
              </div>
              <div v-else-if="qrCodeData.anonymous" class="text-center py-3">
                <i
                  class="fa fa-exclamation-triangle fa-3x text-warning mb-3"
                ></i>
                <h5>Uso Individual Obrigatório</h5>
                <p>
                  Este questionário está configurado como
                  <strong>Anônimo (Link único)</strong>. Por segurança, ele não
                  permite acesso via QR Code genérico.
                </p>
                <p class="text-muted">
                  Utilize a aba <strong>"Distribuir"</strong> para enviar links
                  exclusivos por e-mail aos participantes.
                </p>
              </div>
              <div v-else-if="qrCodeData.qrCode" class="text-center">
                <img
                  :src="qrCodeData.qrCode"
                  alt="QR Code"
                  class="img-fluid mb-3"
                  style="max-width: 300px"
                />
                <p class="text-muted small">{{ qrCodeData.url }}</p>
                <b-row class="justify-content-center">
                  <b-col md="6">
                    <b-button variant="primary" @click="downloadQRCode" block>
                      <i class="fa fa-download"></i> Baixar QR Code
                    </b-button>
                  </b-col>
                  <b-col md="6">
                    <b-button variant="secondary" @click="copyURL" block>
                      <i class="fa fa-copy"></i> Copiar Link
                    </b-button>
                  </b-col>
                </b-row>
              </div>
            </div>
          </b-tab>

          <!-- Distribuir -->
          <b-tab title="Distribuir" :disabled="sidebar.isNew">
            <NR1Distribution
              v-if="sidebar.show && sidebar.tabIndex === 2 && !sidebar.isNew"
              :questionnaire-id="sidebar.questionnaireId"
              @cancel="sidebar.show = false"
            />
          </b-tab>

          <!-- Análise IA -->
          <b-tab title="Análise IA" :disabled="sidebar.isNew">
            <div
              v-if="sidebar.show && sidebar.tabIndex === 3 && !sidebar.isNew"
            >
              <div v-if="aiAnalysis.loading" class="text-center py-5">
                <div class="mb-3">
                  <i class="fas fa-robot fa-3x text-primary blink"></i>
                </div>
                <h5 class="text-primary">Processando Análise...</h5>
                <p class="text-muted">
                  A Inteligência Artificial está consolidando e interpretando as
                  respostas do questionário. Por favor, aguarde.
                </p>
                <b-progress
                  :value="100"
                  variant="primary"
                  striped
                  animated
                  class="mt-3 mx-auto"
                  style="max-width: 300px; height: 10px"
                ></b-progress>
              </div>

              <div v-else class="ai-analysis-content">
                <div class="ai-filter-panel mb-4">
                  <div class="ai-filter-copy">
                    <h6 class="mb-1 text-primary">
                      <i class="fas fa-industry mr-2"></i> Escopo da análise
                    </h6>
                    <p class="mb-0 text-muted small">
                      Selecione <strong>Todos</strong> para gerar uma visão
                      consolidada com comparação entre setores, ou escolha um
                      setor específico para uma análise focada.
                    </p>
                  </div>

                  <b-form-group
                    label="Setor"
                    label-for="ai-analysis-area-select"
                    class="mb-0 ai-filter-group"
                  >
                    <b-form-select
                      id="ai-analysis-area-select"
                      v-model="aiAnalysis.selectedArea"
                      :options="areaSelectOptions"
                      :disabled="aiAnalysis.loadingAreas || aiAnalysis.loading"
                      class="ai-area-select"
                    ></b-form-select>
                    <small v-if="aiAnalysis.loadingAreas" class="text-muted">
                      Carregando setores...
                    </small>
                  </b-form-group>
                </div>

                <div v-if="aiAnalysis.html" class="ai-report-container">
                  <div
                    class="d-flex justify-content-between align-items-center mb-3"
                  >
                    <div>
                      <h5 class="mb-0 text-primary">
                        <i class="fas fa-magic mr-2"></i> Relatório Analítico de
                        IA
                      </h5>
                      <small class="text-muted">
                        Escopo: <strong>{{ aiAnalysisScopeLabel }}</strong>
                      </small>
                    </div>
                    <b-button
                      variant="outline-danger"
                      size="sm"
                      @click="downloadAiPdf"
                      :disabled="aiAnalysis.exportingPdf"
                    >
                      <b-spinner
                        v-if="aiAnalysis.exportingPdf"
                        small
                        class="mr-1"
                      ></b-spinner>
                      <i v-else class="fa fa-file-pdf mr-1"></i>
                      {{
                        aiAnalysis.exportingPdf ? "Gerando..." : "Exportar PDF"
                      }}
                    </b-button>
                  </div>

                  <div id="ai-report-printable">
                    <!-- Texto da análise gerado pela IA -->
                    <div
                      class="border rounded p-4 bg-white shadow-sm mb-4"
                      v-html="aiAnalysis.html"
                    ></div>

                    <!-- Gráficos renderizados como componentes Vue (fora do v-html) -->
                    <div v-if="aiAnalysis.analytics" class="ai-charts-section">
                      <!-- Gráfico de Barras - Score por Categoria -->
                      <b-card
                        v-if="aiBarSeries.length > 0"
                        class="mb-3 shadow-sm"
                        no-body
                      >
                        <b-card-header>
                          <h6 class="mb-0">
                            <i class="fas fa-chart-bar mr-2 text-primary"></i
                            >Score de Risco por Categoria
                          </h6>
                        </b-card-header>
                        <b-card-body>
                          <apexchart
                            type="bar"
                            height="300"
                            :options="aiBarOptions"
                            :series="aiBarSeries"
                          ></apexchart>
                        </b-card-body>
                      </b-card>

                      <!-- Gráfico Radar - Riscos por Categoria -->
                      <b-card
                        v-if="
                          aiRadarSeries.length > 0 &&
                          aiRadarSeries[0].data.length > 0
                        "
                        class="mb-3 shadow-sm"
                        no-body
                      >
                        <b-card-header>
                          <h6 class="mb-0">
                            <i class="fas fa-project-diagram mr-2 text-info"></i
                            >Radar de Riscos por Categoria
                          </h6>
                        </b-card-header>
                        <b-card-body>
                          <apexchart
                            type="radar"
                            height="450"
                            :options="aiRadarOptions"
                            :series="aiRadarSeries"
                          ></apexchart>
                        </b-card-body>
                      </b-card>

                      <!-- Heatmap - Matriz de Risco (Grid CSS igual ao dashboard) -->
                      <b-card
                        v-if="aiHeatMapMatrix"
                        class="mb-3 shadow-sm"
                        no-body
                      >
                        <b-card-header>
                          <h6 class="mb-0">
                            <i class="fas fa-th mr-2 text-warning"></i>Matriz de
                            Calor (Severidade x Probabilidade)
                          </h6>
                        </b-card-header>
                        <b-card-body>
                          <div class="risk-matrix-container">
                            <div class="y-axis-label">Severidade</div>
                            <div class="matrix-grid">
                              <div
                                class="matrix-row"
                                v-for="s in [3, 2, 1, 0]"
                                :key="'s' + s"
                              >
                                <div class="s-label">{{ s + 1 }}</div>
                                <div
                                  v-for="p in [0, 1, 2, 3, 4]"
                                  :key="'p' + p"
                                  class="matrix-cell"
                                  :style="{
                                    backgroundColor: getAiCellColor(s, p),
                                  }"
                                  :class="{
                                    'has-data': aiHeatMapMatrix[s][p].count > 0,
                                  }"
                                  @click="selectAiQuadrant(s, p)"
                                >
                                  <span
                                    v-if="aiHeatMapMatrix[s][p].count > 0"
                                    class="risk-count"
                                  >
                                    {{ aiHeatMapMatrix[s][p].count }}
                                  </span>
                                </div>
                              </div>
                              <div class="matrix-row labels-row">
                                <div class="s-label"></div>
                                <div
                                  class="p-label"
                                  v-for="option in [
                                    { value: 1, label: 'Nunca' },
                                    { value: 2, label: 'Raramente' },
                                    { value: 3, label: 'Às vezes' },
                                    { value: 4, label: 'Frequentemente' },
                                    { value: 5, label: 'Sempre' },
                                  ]"
                                  :key="'pl' + option.value"
                                >
                                  {{ option.label }}
                                </div>
                              </div>
                              <div class="x-axis-label text-center mt-2">
                                Probabilidade
                              </div>
                            </div>
                          </div>
                          <div
                            v-if="aiSelectedQuadrantItems"
                            class="mt-3 quadrant-details"
                          >
                            <div
                              class="d-flex justify-content-between align-items-center mb-2"
                            >
                              <h6 class="mb-0 small font-weight-bold">
                                Itens Selecionados no Quadrante:
                              </h6>
                              <b-button
                                size="sm"
                                variant="link"
                                class="p-0"
                                @click="aiSelectedQuadrantItems = null"
                                >Limpar</b-button
                              >
                            </div>
                            <div
                              class="list-group list-group-flush border rounded"
                              style="max-height: 200px; overflow-y: auto"
                            >
                              <div
                                v-for="(item, idx) in aiSelectedQuadrantItems"
                                :key="idx"
                                class="list-group-item small p-2"
                              >
                                <strong>{{ item.area }}</strong
                                >: {{ item.categoryName }} (Score:
                                {{ item.riskScore }})
                              </div>
                            </div>
                          </div>
                        </b-card-body>
                      </b-card>
                    </div>
                  </div>
                  <!-- end ai-report-printable -->
                </div>

                <div v-else class="text-center py-5 ai-generate-container">
                  <div class="ai-icon mb-3">
                    <i
                      class="fas fa-robot"
                      style="font-size: 3rem; color: #6c757d"
                    ></i>
                  </div>
                  <h5 class="mb-2" style="color: #495057">
                    Relatório Especializado com IA
                  </h5>
                  <p
                    class="text-muted mb-4"
                    style="max-width: 400px; margin: 0 auto"
                  >
                    A Inteligência Artificial pode gerar um relatório detalhado
                    apontando os principais riscos,
                    {{
                      aiAnalysis.selectedArea === "Todos"
                        ? "comparando o comportamento entre setores"
                        : "aprofundando os dados do setor selecionado"
                    }}
                    e recomendações automáticas para este questionário.
                  </p>
                  <b-button
                    variant="primary"
                    @click="generateAiAnalysis"
                    size="lg"
                    :disabled="aiAnalysis.loadingAreas"
                  >
                    <i class="fas fa-magic mr-2"></i> Gerar Relatório IA
                  </b-button>
                  <p
                    v-if="aiAnalysis.loadingAreas"
                    class="text-muted small mt-3 mb-0"
                  >
                    Aguarde o carregamento dos setores para iniciar a análise.
                  </p>
                  <p
                    v-else-if="
                      aiAnalysis.availableAreas.length === 0 &&
                      aiAnalysis.selectedArea === 'Todos'
                    "
                    class="text-muted small mt-3 mb-0"
                  >
                    Nenhum setor específico foi encontrado; a IA usará os dados
                    consolidados disponíveis.
                  </p>
                </div>
              </div>
            </div>
          </b-tab>
        </b-tabs>
      </div>
    </b-sidebar>
  </div>
</template>

<script>
import {
  createDashboard,
  getQuestionnaires,
  getDashboards,
  cloneQuestionnaire,
  deleteQuestionnaire,
  generateQRCode,
  downloadQRCode,
  getParticipations,
  getAiAnalysis,
  getAnalytics,
  getAreas,
  updateDashboard,
} from "@/api/nr1";
import NR1QuestionnaireForm from "./NR1QuestionnaireForm.vue";
import NR1Distribution from "./NR1Distribution.vue";
import VueApexCharts from "vue-apexcharts";

export default {
  name: "NR1QuestionnaireList",
  components: {
    NR1QuestionnaireForm,
    NR1Distribution,
    apexchart: VueApexCharts,
  },
  data() {
    return {
      mainTabIndex: 0,
      loading: false,
      dashboardsLoading: false,
      dashboardOptionsLoading: false,
      questionnaires: [],
      dashboards: [],
      dashboardQuestionnaireOptions: [],
      dashboardQuestionnaireSearch: "",
      dashboardQuestionnairePage: 1,
      dashboardQuestionnairePageSize: 5,
      dashboardQuestionnaireSortBy: "title",
      dashboardQuestionnaireSortDesc: false,
      dashboardPageSizeOptions: [
        { value: 5, text: "5" },
        { value: 10, text: "10" },
        { value: 15, text: "15" },
      ],
      filter: {
        active: null,
      },
      fields: [
        { key: "title", label: "Título", sortable: true },
        { key: "active", label: "Status", sortable: true },
        { key: "stats", label: "Participação" },
        { key: "dates", label: "Período" },
        { key: "actions", label: "Ações", class: "text-right" },
      ],
      dashboardFields: [
        { key: "name", label: "Nome", sortable: true },
        { key: "actions", label: "Ações", class: "text-right" },
      ],
      dashboardQuestionnaireFields: [
        {
          key: "selected",
          label: "",
          class: "dashboard-select-column",
          sortable: false,
        },
        { key: "title", label: "Questionário", sortable: true },
      ],
      sidebar: {
        show: false,
        isNew: false,
        questionnaireId: null,
        tabIndex: 0,
        initialTemplateKey: null,
      },
      templateSelector: {
        show: false,
        selected: null,
        options: [
          {
            key: "risk-assessment",
            title: "Avaliação de Riscos (NR1)",
            description:
              "Modelo padrão para diagnóstico de riscos ocupacionais conforme NR1.",
            disabled: false,
          },
          {
            key: "information-security",
            title: "Segurança da Informação",
            description: "Modelo em desenvolvimento.",
            disabled: true,
          },
        ],
      },
      qrCodeData: {
        loading: false,
        questionnaireId: null,
        qrCode: null,
        url: null,
        anonymous: false,
      },
      aiAnalysis: {
        loading: false,
        html: null,
        analytics: null,
        exportingPdf: false,
        loadingAreas: false,
        availableAreas: [],
        selectedArea: "Todos",
      },
      cloneModal: {
        show: false,
        loading: false,
        sourceItem: null,
        title: "",
        submitted: false,
      },
      dashboardModal: {
        show: false,
        loading: false,
        submitted: false,
        id: null,
        name: "",
        questionnaireIds: [],
      },
      aiHeatMapMatrix: null,
      aiSelectedQuadrantItems: null,
    };
  },
  watch: {
    "sidebar.tabIndex": function (newVal) {
      if (newVal === 1 && !this.sidebar.isNew) {
        this.fetchQRCode(this.sidebar.questionnaireId);
      }

      if (newVal === 3 && !this.sidebar.isNew) {
        this.loadAiAreas();
      }
    },
    "sidebar.show": function (newVal) {
      if (!newVal) {
        // clean up when sidebar closses
        setTimeout(() => {
          this.sidebar.questionnaireId = null;
          this.sidebar.isNew = false;
          this.sidebar.tabIndex = 0;
          this.sidebar.initialTemplateKey = null;
          this.aiAnalysis.html = null;
          this.aiAnalysis.analytics = null;
          this.aiAnalysis.exportingPdf = false;
          this.aiAnalysis.loadingAreas = false;
          this.aiAnalysis.availableAreas = [];
          this.aiAnalysis.selectedArea = "Todos";
          this.aiHeatMapMatrix = null;
          this.aiSelectedQuadrantItems = null;
          this.resetQrCodeData();
        }, 300);
      }
    },
    "sidebar.questionnaireId": function (newVal, oldVal) {
      if (newVal && newVal !== oldVal && this.sidebar.tabIndex === 1) {
        this.fetchQRCode(newVal);
      }
    },
    "aiAnalysis.selectedArea": function () {
      this.resetAiAnalysisContent();
    },
    dashboardQuestionnaireSearch() {
      this.dashboardQuestionnairePage = 1;
    },
    dashboardQuestionnairePageSize() {
      this.dashboardQuestionnairePage = 1;
    },
  },
  computed: {
    isElevatedRole() {
      const role = this.$store.getters.profile?.role;
      return role === "root" || role === "company-root";
    },
    questionnairePermissions() {
      return this.$store.getters.permissions?.questionnaire || {};
    },
    canReadQuestionnaire() {
      return this.isElevatedRole || this.questionnairePermissions.read === true;
    },
    canCreateQuestionnaire() {
      return (
        this.isElevatedRole || this.questionnairePermissions.create === true
      );
    },
    canEditQuestionnaire() {
      return this.isElevatedRole || this.questionnairePermissions.edit === true;
    },
    areaSelectOptions() {
      return [
        { value: "Todos", text: "Todos" },
        ...this.aiAnalysis.availableAreas.map((area) => ({
          value: area,
          text: area,
        })),
      ];
    },
    aiAnalysisScopeLabel() {
      return this.aiAnalysis.selectedArea === "Todos"
        ? "Todos os setores"
        : this.aiAnalysis.selectedArea;
    },
    cloneTitleState() {
      if (!this.cloneModal.submitted && !this.cloneModal.title) {
        return null;
      }

      return Boolean(this.cloneModal.title && this.cloneModal.title.trim());
    },
    dashboardNameState() {
      if (!this.dashboardModal.submitted && !this.dashboardModal.name) {
        return null;
      }

      return Boolean(
        this.dashboardModal.name && this.dashboardModal.name.trim()
      );
    },
    availableDashboardQuestionnaires() {
      return (this.dashboardQuestionnaireOptions || []).filter(
        (item) => item.active !== false
      );
    },
    filteredDashboardQuestionnaires() {
      const search = (this.dashboardQuestionnaireSearch || "")
        .trim()
        .toLowerCase();

      if (!search) {
        return this.availableDashboardQuestionnaires;
      }

      return this.availableDashboardQuestionnaires.filter((item) => {
        const title = String(item.title || "").toLowerCase();
        const description = String(item.description || "").toLowerCase();
        return title.includes(search) || description.includes(search);
      });
    },
    paginatedDashboardQuestionnaires() {
      const start =
        (this.dashboardQuestionnairePage - 1) *
        this.dashboardQuestionnairePageSize;
      return this.filteredDashboardQuestionnaires.slice(
        start,
        start + this.dashboardQuestionnairePageSize
      );
    },
    dashboardQuestionnaireRangeStart() {
      if (!this.filteredDashboardQuestionnaires.length) {
        return 0;
      }

      return (
        (this.dashboardQuestionnairePage - 1) *
          this.dashboardQuestionnairePageSize +
        1
      );
    },
    dashboardQuestionnaireRangeEnd() {
      if (!this.filteredDashboardQuestionnaires.length) {
        return 0;
      }

      return Math.min(
        this.dashboardQuestionnairePage * this.dashboardQuestionnairePageSize,
        this.filteredDashboardQuestionnaires.length
      );
    },
    saveDashboardLabel() {
      return this.dashboardModal.id ? "Salvar alterações" : "Criar";
    },
    aiBarOptions() {
      const cats = this.aiAnalysis.analytics?.categoryRisks || [];
      return {
        chart: { type: "bar", toolbar: { show: false } },
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true,
            borderRadius: 4,
            barHeight: "60%",
          },
        },
        xaxis: {
          categories: cats.map((c) => c.categoryName || "Desconhecido"),
        },
        colors: cats.map((c) => {
          if (c.riskLevel === "Crítico") return "#dc2626";
          if (c.riskLevel === "Alto") return "#ea580c";
          if (c.riskLevel === "Médio") return "#eab308";
          return "#22c55e";
        }),
        dataLabels: {
          enabled: true,
          style: { fontSize: "12px", fontWeight: "600", colors: ["#fff"] },
        },
        legend: { show: false },
        tooltip: {
          y: {
            formatter: (val) => `Score: ${val}`,
          },
        },
      };
    },
    aiBarSeries() {
      const cats = this.aiAnalysis.analytics?.categoryRisks || [];
      if (cats.length === 0) return [];
      return [
        {
          name: "Score de Risco",
          data: cats.map((c) => c.riskScore),
        },
      ];
    },
    aiRadarOptions() {
      const cats = this.aiAnalysis.analytics?.categoryRisks || [];
      return {
        chart: { type: "radar", toolbar: { show: false } },
        plotOptions: {
          radar: {
            size: 180,
            polygons: {
              strokeColors: "#e2e8f0",
              connectorColors: "#e2e8f0",
            },
          },
        },
        xaxis: {
          categories: cats.map((c) => c.categoryName || "Desconhecido"),
          labels: { style: { fontSize: "12px" } },
        },
        yaxis: { max: 20 },
        stroke: { width: 2 },
        fill: { opacity: 0.4 },
        colors: ["#00E396"],
        markers: { size: 4 },
      };
    },
    aiRadarSeries() {
      const cats = this.aiAnalysis.analytics?.categoryRisks || [];
      if (cats.length === 0) return [];
      return [
        {
          name: "Score de Risco",
          data: cats.map((c) => c.riskScore),
        },
      ];
    },
  },
  mounted() {
    this.loadInitialData();
  },
  methods: {
    async loadInitialData() {
      await Promise.all([
        this.loadQuestionnaires(),
        this.loadDashboards(),
        this.loadDashboardQuestionnaireOptions(),
      ]);
    },
    dashboardQuestionnaireRowClass() {
      return "complaints-layout-row";
    },
    resetQrCodeData() {
      this.qrCodeData.loading = false;
      this.qrCodeData.questionnaireId = null;
      this.qrCodeData.qrCode = null;
      this.qrCodeData.url = null;
      this.qrCodeData.anonymous = false;
    },
    resetAiAnalysisContent() {
      this.aiAnalysis.html = null;
      this.aiAnalysis.analytics = null;
      this.aiHeatMapMatrix = null;
      this.aiSelectedQuadrantItems = null;
    },
    async loadAiAreas() {
      if (
        this.aiAnalysis.loadingAreas ||
        this.aiAnalysis.availableAreas.length
      ) {
        return;
      }

      this.aiAnalysis.loadingAreas = true;
      try {
        const response = await getAreas();
        this.aiAnalysis.availableAreas = Array.isArray(response.data)
          ? response.data
          : [];
      } catch (error) {
        console.error("Error loading AI analysis areas:", error);
        this.$bvToast.toast("Erro ao carregar setores para a análise de IA", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 8000,
        });
      } finally {
        this.aiAnalysis.loadingAreas = false;
      }
    },
    getAiAnalysisParams() {
      const params = {};

      if (this.aiAnalysis.selectedArea !== "Todos") {
        params.area = this.aiAnalysis.selectedArea;
      }

      return params;
    },
    async loadQuestionnaires() {
      this.loading = true;
      try {
        const params = {};
        if (this.filter.active !== null) {
          params.active = this.filter.active;
        }

        const response = await getQuestionnaires(params);
        this.questionnaires = response.data;
      } catch (error) {
        console.error("Error loading questionnaires:", error);
        this.$notify({
          type: "error",
          title: "Erro",
          text: "Erro ao carregar questionários",
        });
      } finally {
        this.loading = false;
      }
    },
    async loadDashboards() {
      this.dashboardsLoading = true;
      try {
        const response = await getDashboards();
        this.dashboards = response.data || [];
      } catch (error) {
        console.error("Error loading dashboards:", error);
        this.$notify({
          type: "error",
          title: "Erro",
          text: "Erro ao carregar dashboards",
        });
      } finally {
        this.dashboardsLoading = false;
      }
    },
    async loadDashboardQuestionnaireOptions() {
      this.dashboardOptionsLoading = true;
      try {
        const response = await getQuestionnaires({ active: true });
        this.dashboardQuestionnaireOptions = response.data || [];
      } catch (error) {
        console.error("Error loading dashboard questionnaire options:", error);
        this.$notify({
          type: "error",
          title: "Erro",
          text: "Erro ao carregar questionários para o dashboard",
        });
      } finally {
        this.dashboardOptionsLoading = false;
      }
    },
    onRowClicked(item) {
      if (!this.canEditQuestionnaire) {
        return;
      }

      this.sidebar.isNew = false;
      this.sidebar.questionnaireId = item._id;
      this.sidebar.tabIndex = 0;
      this.sidebar.show = true;
    },
    questionnaireTitle(id) {
      if (!id) return "";
      const normalizedId = String(id);
      const q = this.questionnaires.find((q) => String(q._id) === normalizedId);
      return q ? q.title : "";
    },
    createQuestionnaire() {
      if (!this.canCreateQuestionnaire) {
        return;
      }

      this.templateSelector.selected = null;
      this.templateSelector.show = true;
    },
    selectTemplate(templateKey) {
      this.templateSelector.selected = templateKey;
    },
    closeTemplateSelector() {
      this.templateSelector.show = false;
      this.templateSelector.selected = null;
    },
    continueWithSelectedTemplate() {
      if (!this.templateSelector.selected) {
        return;
      }

      this.templateSelector.show = false;
      this.sidebar.isNew = true;
      this.sidebar.questionnaireId = null;
      this.sidebar.tabIndex = 0;
      this.sidebar.initialTemplateKey = this.templateSelector.selected;
      this.sidebar.show = true;
      this.templateSelector.selected = null;
    },
    onQuestionnaireSaved() {
      this.sidebar.show = false;
      this.loadQuestionnaires();
      this.loadDashboardQuestionnaireOptions();
    },
    openCreateDashboardModal() {
      if (!this.canCreateQuestionnaire) {
        return;
      }

      this.dashboardModal.id = null;
      this.dashboardModal.name = "";
      this.dashboardModal.questionnaireIds = [];
      this.dashboardModal.submitted = false;
      this.dashboardQuestionnaireSearch = "";
      this.dashboardQuestionnairePage = 1;
      this.dashboardModal.show = true;
      this.loadDashboardQuestionnaireOptions();
    },
    openEditDashboardModal(item) {
      if (!this.canEditQuestionnaire) {
        return;
      }

      this.dashboardModal.id = item._id;
      this.dashboardModal.name = item.name || "";
      this.dashboardModal.questionnaireIds = Array.isArray(
        item.questionnaireIds
      )
        ? item.questionnaireIds.map((value) => parseInt(value, 10))
        : [];
      this.dashboardModal.submitted = false;
      this.dashboardQuestionnaireSearch = "";
      this.dashboardQuestionnairePage = 1;
      this.dashboardModal.show = true;
      this.loadDashboardQuestionnaireOptions();
    },
    resetDashboardModal() {
      this.dashboardModal.loading = false;
      this.dashboardModal.submitted = false;
      this.dashboardModal.id = null;
      this.dashboardModal.name = "";
      this.dashboardModal.questionnaireIds = [];
      this.dashboardQuestionnaireSearch = "";
      this.dashboardQuestionnairePage = 1;
    },
    isDashboardQuestionnaireSelected(questionnaireId) {
      return this.dashboardModal.questionnaireIds.includes(questionnaireId);
    },
    toggleDashboardQuestionnaire(questionnaireId, checked) {
      if (checked) {
        if (!this.dashboardModal.questionnaireIds.includes(questionnaireId)) {
          this.dashboardModal.questionnaireIds = [
            ...this.dashboardModal.questionnaireIds,
            questionnaireId,
          ];
        }
        return;
      }

      this.dashboardModal.questionnaireIds =
        this.dashboardModal.questionnaireIds.filter(
          (item) => item !== questionnaireId
        );
    },
    dashboardQuestionnaireSummary(item) {
      const questionnaires = Array.isArray(item.questionnaires)
        ? item.questionnaires
        : [];

      if (!questionnaires.length) {
        return "Nenhum questionário vinculado";
      }

      if (questionnaires.length === 1) {
        return questionnaires[0].title;
      }

      return `${questionnaires.length} questionários selecionados`;
    },
    viewDashboard(item) {
      this.$router.push(`/nr1/dashboards/${item._id}`);
    },
    async submitDashboard() {
      if (
        (!this.dashboardModal.id && !this.canCreateQuestionnaire) ||
        (this.dashboardModal.id && !this.canEditQuestionnaire)
      ) {
        return;
      }

      this.dashboardModal.submitted = true;

      if (
        !this.dashboardModal.name ||
        !this.dashboardModal.name.trim() ||
        !this.dashboardModal.questionnaireIds.length
      ) {
        return;
      }

      this.dashboardModal.loading = true;

      const payload = {
        name: this.dashboardModal.name.trim(),
        questionnaireIds: this.dashboardModal.questionnaireIds,
      };

      try {
        if (this.dashboardModal.id) {
          await updateDashboard(this.dashboardModal.id, payload);
        } else {
          await createDashboard(payload);
        }

        this.dashboardModal.show = false;
        this.$notify({
          type: "success",
          title: "Sucesso",
          text: this.dashboardModal.id
            ? "Dashboard atualizado com sucesso"
            : "Dashboard criado com sucesso",
        });
        await this.loadDashboards();
      } catch (error) {
        console.error("Error saving dashboard:", error);
        this.$notify({
          type: "error",
          title: "Erro",
          text: error.response?.data?.message || "Erro ao salvar dashboard",
        });
      } finally {
        this.dashboardModal.loading = false;
      }
    },
    openCloneModal(item) {
      if (!this.canCreateQuestionnaire) {
        return;
      }

      this.cloneModal.sourceItem = item;
      this.cloneModal.title = "";
      this.cloneModal.submitted = false;
      this.cloneModal.show = true;
    },
    resetCloneModal() {
      this.cloneModal.loading = false;
      this.cloneModal.sourceItem = null;
      this.cloneModal.title = "";
      this.cloneModal.submitted = false;
    },
    focusCloneTitleInput() {
      this.$nextTick(() => {
        const input = this.$refs.cloneTitleInput;
        if (input && typeof input.focus === "function") {
          input.focus();
        }
      });
    },
    async confirmCloneQuestionnaire() {
      if (!this.canCreateQuestionnaire) {
        return;
      }

      this.cloneModal.submitted = true;

      const title = (this.cloneModal.title || "").trim();
      if (!title || !this.cloneModal.sourceItem?._id) {
        return;
      }

      this.cloneModal.loading = true;

      try {
        await cloneQuestionnaire(this.cloneModal.sourceItem._id, { title });
        this.cloneModal.show = false;
        this.$notify({
          type: "success",
          title: "Sucesso",
          text: "Questionário clonado com sucesso",
        });
        await this.loadQuestionnaires();
        await this.loadDashboardQuestionnaireOptions();
      } catch (error) {
        console.error("Error cloning questionnaire:", error);
        this.$notify({
          type: "error",
          title: "Erro",
          text: error.response?.data?.message || "Erro ao clonar questionário",
        });
      } finally {
        this.cloneModal.loading = false;
      }
    },
    editQuestionnaire(item) {
      if (!this.canEditQuestionnaire) {
        return;
      }

      this.onRowClicked(item);
    },
    viewAnalytics(item) {
      if (this.isDraftQuestionnaire(item)) {
        return;
      }
      this.$router.push(`/nr1/questionarios/${item._id}/analytics`);
    },
    viewObservations(item) {
      if (this.isDraftQuestionnaire(item)) {
        return;
      }
      this.$router.push(`/nr1/questionarios/${item._id}/observacoes`);
    },
    manageDistribution(item) {
      if (!this.canEditQuestionnaire) {
        return;
      }

      if (this.isDraftQuestionnaire(item)) {
        this.$notify({
          type: "warning",
          title: "Rascunho",
          text: "Publique o questionário antes de distribuir.",
        });
        return;
      }
      this.sidebar.isNew = false;
      this.sidebar.questionnaireId = item._id;
      this.sidebar.tabIndex = 2; // Distribuir
      this.sidebar.show = true;
    },
    isQRCodeDisabled(item) {
      return this.isDraftQuestionnaire(item) || item?.anonymousResponses;
    },
    qrCodeActionTitle(item) {
      if (item?.anonymousResponses) {
        return "QR Code indisponível para link de uso único";
      }

      if (this.isDraftQuestionnaire(item)) {
        return "Publique o questionário para gerar QR Code";
      }

      return "QR Code";
    },
    generateQRCode(item) {
      if (!this.canReadQuestionnaire) {
        return;
      }

      if (this.isDraftQuestionnaire(item)) {
        this.$notify({
          type: "warning",
          title: "Rascunho",
          text: "Publique o questionário antes de gerar QR Code.",
        });
        return;
      }

      if (!this.canEditQuestionnaire) {
        return;
      }

      if (item?.anonymousResponses) {
        return;
      }

      if (String(this.qrCodeData.questionnaireId) !== String(item._id)) {
        this.resetQrCodeData();
      }

      this.sidebar.isNew = false;
      this.sidebar.questionnaireId = item._id;
      this.sidebar.tabIndex = 1; // QR Code
      this.sidebar.show = true;

      this.$nextTick(() => {
        this.fetchQRCode(item._id);
      });
    },
    openAiAnalysis(item) {
      if (this.isDraftQuestionnaire(item)) {
        this.$notify({
          type: "warning",
          title: "Rascunho",
          text: "Publique o questionário antes de abrir a análise IA.",
        });
        return;
      }
      this.sidebar.isNew = false;
      this.sidebar.questionnaireId = item._id;
      this.sidebar.tabIndex = 3; // Análise IA
      this.sidebar.show = true;
      this.loadAiAreas();
    },
    isDraftQuestionnaire(item) {
      return item && item.isDraft === true;
    },
    statusLabel(item) {
      if (this.isDraftQuestionnaire(item)) {
        return "Rascunho";
      }

      return item && item.active ? "Ativo" : "Inativo";
    },
    statusBadgeClass(item) {
      if (this.isDraftQuestionnaire(item)) {
        return "badge-soft-warning";
      }

      return item && item.active
        ? "badge-soft-success"
        : "badge-soft-secondary";
    },
    async fetchQRCode(id) {
      const normalizedId = String(id);

      if (
        this.qrCodeData.questionnaireId === normalizedId &&
        (this.qrCodeData.qrCode || this.qrCodeData.anonymous)
      ) {
        return;
      }

      const item = this.questionnaires.find(
        (q) => String(q._id) === normalizedId
      );
      if (!item) return;

      this.resetQrCodeData();
      this.qrCodeData.loading = true;
      this.qrCodeData.questionnaireId = normalizedId;
      this.qrCodeData.anonymous = item.anonymousResponses;

      if (this.qrCodeData.anonymous) {
        this.qrCodeData.loading = false;
        return;
      }

      try {
        const response = await generateQRCode(item._id);
        this.qrCodeData.qrCode = response.data.qrCode;
        this.qrCodeData.url = response.data.url;
      } catch (error) {
        console.error("Error generating QR code:", error);
        this.$notify({
          type: "error",
          title: "Erro",
          text: "Erro ao gerar QR Code",
        });
      } finally {
        this.qrCodeData.loading = false;
      }
    },
    async downloadQRCode() {
      try {
        const response = await downloadQRCode(this.sidebar.questionnaireId, {
          responseType: "blob",
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `nr1-qrcode-${this.sidebar.questionnaireId}.png`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();

        this.$notify({
          type: "success",
          title: "Sucesso",
          text: "QR Code baixado com sucesso",
        });
      } catch (error) {
        console.error("Error downloading QR code:", error);
        this.$notify({
          type: "error",
          title: "Erro",
          text: "Erro ao baixar QR Code",
        });
      }
    },
    copyURL() {
      navigator.clipboard.writeText(this.qrCodeData.url);
      this.$notify({
        type: "success",
        title: "Sucesso",
        text: "Link copiado para a área de transferência",
      });
    },
    async generateAiAnalysis() {
      if (!this.sidebar.questionnaireId) return;

      this.aiAnalysis.loading = true;
      this.resetAiAnalysisContent();

      try {
        const params = this.getAiAnalysisParams();

        // Fetch AI report and analytics data in parallel
        const [aiResponse, analyticsResp] = await Promise.all([
          getAiAnalysis(this.sidebar.questionnaireId, params),
          getAnalytics(this.sidebar.questionnaireId, params),
        ]);

        let rawHtml = aiResponse.data.htmlReport;
        // Clean up any markdown code block wrappers
        rawHtml = rawHtml.replace(/^```html\s*/i, "").replace(/```\s*$/i, "");

        this.aiAnalysis.html = rawHtml;
        this.aiAnalysis.analytics = analyticsResp.data;
        this.buildAiHeatMap();
      } catch (error) {
        console.error("Error generating AI analysis:", error);
        this.$bvToast.toast(
          error.response?.data?.message || "Erro ao processar análise da IA",
          {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 6000,
          }
        );
      } finally {
        this.aiAnalysis.loading = false;
      }
    },
    buildAiHeatMap() {
      const matrix = Array(4)
        .fill()
        .map(() => Array(5).fill({ count: 0, items: [] }));
      if (this.aiAnalysis.analytics?.matrix) {
        this.aiAnalysis.analytics.matrix.forEach((cell) => {
          const sIdx = cell.severity - 1;
          const pIdx = cell.probability - 1;
          if (sIdx >= 0 && sIdx < 4 && pIdx >= 0 && pIdx < 5) {
            matrix[sIdx][pIdx] = {
              count: cell.count,
              items: cell.items || [],
            };
          }
        });
      }
      this.aiHeatMapMatrix = matrix;
    },
    getAiCellColor(s, p) {
      const score = (s + 1) * (p + 1);
      if (score >= 16) return "#ffcdd2";
      if (score >= 11) return "#ffe0b2";
      if (score >= 6) return "#fff9c4";
      return "#c8e6c9";
    },
    selectAiQuadrant(s, p) {
      this.aiSelectedQuadrantItems = this.aiHeatMapMatrix[s][p].items;
    },
    downloadAiPdf() {
      const element = document.getElementById("ai-report-printable");
      if (!element) return;

      this.aiAnalysis.exportingPdf = true;

      try {
        // Get the title for the PDF
        const title =
          this.questionnaireTitle(this.sidebar.questionnaireId) ||
          "Relatório IA";

        // Clone the content
        const clone = element.cloneNode(true);

        // Convert ApexCharts SVGs to inline images for print
        const originalCharts = element.querySelectorAll(".apexcharts-canvas");
        const clonedCharts = clone.querySelectorAll(".apexcharts-canvas");

        originalCharts.forEach((chart, i) => {
          const svg = chart.querySelector("svg");
          if (svg && clonedCharts[i]) {
            const svgData = new XMLSerializer().serializeToString(svg);
            const svgBlob = new Blob([svgData], {
              type: "image/svg+xml;charset=utf-8",
            });
            const url = URL.createObjectURL(svgBlob);
            const img = document.createElement("img");
            img.src = url;
            img.style.width = "100%";
            img.style.maxWidth = "600px";
            img.style.display = "block";
            img.style.margin = "10px auto";
            // Replace the chart container with the img
            const chartParent =
              clonedCharts[i].closest(".apexcharts-canvas")?.parentElement;
            if (chartParent) {
              chartParent.innerHTML = "";
              chartParent.appendChild(img);
            }
          }
        });

        // Open a new window for printing
        const printWindow = window.open("", "_blank");
        if (!printWindow) {
          this.$bvToast.toast("Permita pop-ups para gerar o PDF.", {
            title: "Aviso",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
          });
          return;
        }

        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="utf-8">
            <title>${title} - Análise IA</title>
            <style>
              * { box-sizing: border-box; }
              body {
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                color: #333;
                line-height: 1.6;
                padding: 30px 40px;
                max-width: 900px;
                margin: 0 auto;
                background: #fff;
              }
              h1, h2, h3, h4, h5, h6 {
                color: #1a1a2e;
                margin-top: 1.5em;
                margin-bottom: 0.5em;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin: 15px 0;
                page-break-inside: avoid;
              }
              td, th {
                border: 1px solid #e2e8f0;
                padding: 8px 12px;
                text-align: center;
              }
              th { background: #f8fafc; }
              .card {
                border: 1px solid #e2e8f0;
                border-radius: 8px;
                margin-bottom: 20px;
                page-break-inside: avoid;
                overflow: hidden;
              }
              .card-header {
                background: #f8fafc;
                padding: 12px 16px;
                border-bottom: 1px solid #e2e8f0;
                font-weight: 600;
                font-size: 0.95rem;
              }
              .card-body {
                padding: 16px;
              }
              img {
                max-width: 100%;
                height: auto;
              }
              .print-header {
                text-align: center;
                border-bottom: 2px solid #3b82f6;
                padding-bottom: 15px;
                margin-bottom: 25px;
              }
              .print-header h1 {
                color: #3b82f6;
                margin: 0;
                font-size: 1.4rem;
              }
              .print-header p {
                color: #64748b;
                margin: 5px 0 0;
                font-size: 0.85rem;
              }
              .risk-matrix-container {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: center;
                padding: 20px;
              }
              .y-axis-label {
                writing-mode: vertical-rl;
                transform: rotate(180deg);
                font-weight: bold;
                margin-right: 15px;
                color: #666;
              }
              .matrix-grid {
                display: flex;
                flex-direction: column;
              }
              .matrix-row {
                display: flex;
                flex-direction: row;
              }
              .matrix-cell {
                width: 60px;
                height: 60px;
                border: 1px solid #e2e8f0;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              .matrix-cell.has-data {
                box-shadow: inset 0 0 0 2px rgba(0,0,0,0.1);
              }
              .risk-count {
                font-weight: bold;
                font-size: 1.2rem;
                color: #333;
              }
              .s-label, .p-label {
                width: 60px;
                min-height: 38px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                color: #888;
                font-size: 0.62rem;
                line-height: 1.1;
                text-align: center;
                padding: 0.15rem;
              }
              .s-label {
                width: 30px;
                height: 60px;
                margin-right: 10px;
              }
              .labels-row {
                margin-top: 10px;
              }
              .x-axis-label {
                text-align: center;
                margin-top: 8px;
                font-weight: bold;
                color: #666;
              }
              .quadrant-details { display: none; }
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              @media print {
                body { padding: 15px 20px; }
                .card { break-inside: avoid; }
                table { break-inside: avoid; }
                .risk-matrix-container { break-inside: avoid; }
              }
            </style>
          </head>
          <body>
            <div class="print-header">
              <h1>⚠️ Análise de Riscos — ${title}</h1>
              <p>Gerado por IA em ${new Date().toLocaleDateString(
                "pt-BR"
              )} às ${new Date().toLocaleTimeString("pt-BR")}</p>
            </div>
            ${clone.innerHTML}
          </body>
          </html>
        `);
        printWindow.document.close();

        // Wait for images to load, then print
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };

        // Fallback if onload doesn't fire
        setTimeout(() => {
          if (!printWindow.closed) {
            printWindow.print();
          }
        }, 2000);
      } catch (error) {
        console.error("Error generating PDF:", error);
        this.$bvToast.toast("Não foi possível gerar o PDF.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
        });
      } finally {
        this.aiAnalysis.exportingPdf = false;
      }
    },

    async deleteQuestionnaire(item) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Tem certeza que deseja desativar o questionário "${item.title}"?`,
        {
          title: "Confirmar Desativação",
          okVariant: "danger",
          okTitle: "Desativar",
          cancelTitle: "Cancelar",
        }
      );

      if (confirmed) {
        try {
          await deleteQuestionnaire(item._id);
          this.$notify({
            type: "success",
            title: "Sucesso",
            text: "Questionário desativado com sucesso",
          });
          this.loadQuestionnaires();
          this.loadDashboardQuestionnaireOptions();
        } catch (error) {
          console.error("Error deleting questionnaire:", error);
          this.$notify({
            type: "error",
            title: "Erro",
            text: "Erro ao desativar questionário",
          });
        }
      }
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString("pt-BR");
    },
    getProgressVariant(percentage) {
      if (percentage >= 75) return "success";
      if (percentage >= 50) return "info";
      if (percentage >= 25) return "warning";
      return "danger";
    },
  },
};
</script>

<style scoped>
.modern-card {
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  background-color: #ffffff;
}

.modern-card-header {
  border-bottom: 1px solid #f1f5f9;
  background-color: transparent;
  padding: 1.5rem 1.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.header-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.btn-new {
  border-radius: 8px;
  font-weight: 600;
  padding: 0.5rem 1.25rem;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
  transition: all 0.2s ease;
}

.btn-new-dashboard {
  box-shadow: none;
}

.dashboard-table-name {
  font-weight: 700;
  color: #0f172a;
}

.dashboard-modal-section {
  border-top: 1px solid #e2e8f0;
  padding-top: 1rem;
}

.dashboard-modal-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
}

.dashboard-picker-table {
  margin-bottom: 0;
}

.dashboard-picker-table ::v-deep td:first-child,
.dashboard-picker-table ::v-deep th:first-child {
  width: 48px;
  text-align: center;
}

.dashboard-picker-table ::v-deep tbody td {
  vertical-align: middle;
}

.dashboard-questionnaire-title {
  font-weight: 700;
  color: #0f172a;
}

.dashboard-select-column {
  width: 48px;
}

.dashboard-search-input {
  border-radius: 8px;
  border: 1px solid #cbd5e1;
}

.dashboard-search-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.btn-new:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.custom-select-modern {
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  padding: 0.5rem 1rem;
  height: auto;
  color: #334155;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.custom-select-modern:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  outline: none;
}

.pagination-nav {
  margin-top: 1.5rem;
}

.dashboard-pagination-nav {
  margin-top: 0;
}

.dashboard-pagination-nav ::v-deep .pagination {
  margin-bottom: 0;
}

.dashboard-pagination-nav ::v-deep .page-item .page-link {
  border-radius: 6px;
  margin: 0 3px;
  border: 1px solid #e9ecef;
  color: #495057;
  transition: background-color 0.2s, color 0.2s;
}

.dashboard-pagination-nav ::v-deep .page-item .page-link:hover {
  background-color: #f0f2f5;
  color: #0056b3;
}

.dashboard-pagination-nav ::v-deep .page-item.active .page-link {
  background-color: #007bff;
  border-color: #007bff;
  color: #fff;
}

.dashboard-pagination-nav ::v-deep .page-item.disabled .page-link {
  background-color: transparent;
  color: #adb5bd;
}

.title-text {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2px;
}

.text-sub {
  font-size: 0.85rem;
  color: #64748b;
}

.modern-table {
  background-color: transparent;
  margin-bottom: 0;
}

.modern-table ::v-deep th {
  border-top: none;
  border-bottom: 1px solid #e2e8f0;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  color: #64748b;
  letter-spacing: 0.5px;
  padding: 1rem 1.25rem;
  background-color: #f8fafc;
}

.modern-table ::v-deep th:first-child {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}

.modern-table ::v-deep th:last-child {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}

.modern-table ::v-deep td {
  vertical-align: middle;
  border-bottom: 1px solid #f1f5f9;
  padding: 1.25rem;
  color: #334155;
}

.modern-table ::v-deep tbody tr {
  transition: background-color 0.2s ease;
}

.modern-table ::v-deep tbody tr:hover {
  background-color: #f8fafc;
}

.stats-container,
.dates-container {
  min-width: 140px;
}

.participation-summary {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.participation-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.3rem;
  line-height: 1.3;
}

.participation-line--primary {
  color: #475569;
}

.participation-label {
  font-size: 0.82rem;
  font-weight: 700;
  color: #475569;
}

.participation-strong {
  font-size: 0.92rem;
  font-weight: 700;
  color: #0f172a;
}

.participation-muted {
  font-size: 0.78rem;
  color: #64748b;
}

.participation-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.participation-chip {
  display: inline-flex;
  align-items: center;
  min-height: 1.55rem;
  padding: 0.14rem 0.5rem;
  border-radius: 999px;
  background: #eef4ff;
  color: #355070;
  font-size: 0.74rem;
  font-weight: 600;
}

.modern-progress {
  border-radius: 10px;
  background-color: #e2e8f0;
  box-shadow: none;
}

.status-badge {
  padding: 0.4em 0.8em;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.75rem;
  display: inline-block;
  text-align: center;
}

.badge-soft-success {
  background-color: #dcfce7;
  color: #166534;
}

.badge-soft-secondary {
  background-color: #f1f5f9;
  color: #475569;
}

.badge-soft-warning {
  background-color: #fef3c7;
  color: #92400e;
}

.action-btn {
  width: 36px;
  height: 36px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  margin: 0 4px;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
}

.action-btn i {
  font-size: 0.95rem;
}

.action-btn:active {
  transform: scale(0.95);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-btn:disabled:hover {
  transform: none;
}

.action-btn-info {
  background-color: #e0f2fe;
  color: #0284c7;
}
.action-btn-info:hover {
  background-color: #0284c7;
  color: white;
}

.action-btn-secondary {
  background-color: #f1f5f9;
  color: #475569;
}
.action-btn-secondary:hover {
  background-color: #475569;
  color: white;
}

.action-btn-success {
  background-color: #dcfce7;
  color: #166534;
}
.action-btn-success:hover {
  background-color: #166534;
  color: white;
}

.action-btn-primary {
  background-color: #e0e7ff;
  color: #4338ca;
}
.action-btn-primary:hover {
  background-color: #4338ca;
  color: white;
}

.action-btn-clone {
  background-color: #fff7ed;
  color: #c2410c;
}
.action-btn-clone:hover {
  background-color: #c2410c;
  color: white;
}

.action-btn-danger {
  background-color: #fee2e2;
  color: #dc2626;
}
.action-btn-danger:hover {
  background-color: #dc2626;
  color: white;
}
.action-btn-danger:disabled {
  background-color: #f8fafc;
  color: #cbd5e1;
  cursor: not-allowed;
}

.action-btn-danger:disabled:hover {
  background-color: #f8fafc;
  color: #cbd5e1;
}

.modern-table ::v-deep tbody tr {
  cursor: pointer;
}

.ai-generate-container {
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 2px dashed #dee2e6;
  padding: 3rem 2rem;
}

.template-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.template-card {
  border: 1px solid #dbe4f0;
  border-radius: 10px;
  background: #ffffff;
  padding: 1rem;
  text-align: left;
  transition: all 0.2s ease;
}

.template-card:hover {
  border-color: #93c5fd;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
}

.template-card-selected {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.template-card-disabled {
  opacity: 0.65;
}

.template-card-title {
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.5rem;
}

.template-card-description {
  font-size: 0.85rem;
  color: #64748b;
  margin-bottom: 0.75rem;
}

@media (max-width: 768px) {
  .template-grid {
    grid-template-columns: 1fr;
  }
}

.ai-analysis-content {
  display: flex;
  flex-direction: column;
}

.ai-filter-panel {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
}

.ai-filter-copy {
  flex: 1 1 auto;
}

.ai-filter-group {
  min-width: 240px;
}

.ai-area-select {
  border-radius: 10px;
  min-height: 42px;
}

.ai-report-container {
  animation: fadeIn 0.4s;
}

.blink {
  animation: blinker 1.5s linear infinite;
}

@keyframes blinker {
  50% {
    opacity: 0.5;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.risk-matrix-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.y-axis-label {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-weight: bold;
  margin-right: 15px;
  color: #666;
}

.matrix-grid {
  display: flex;
  flex-direction: column;
}

.matrix-row {
  display: flex;
  flex-direction: row;
}

.matrix-cell {
  width: 60px;
  height: 60px;
  border: 1px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.matrix-cell:hover {
  filter: brightness(0.9);
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.matrix-cell.has-data {
  box-shadow: inset 0 0 0 2px rgba(0, 0, 0, 0.1);
}

.risk-count {
  font-weight: bold;
  font-size: 1.2rem;
  color: #333;
}

@media (max-width: 768px) {
  .ai-filter-panel {
    flex-direction: column;
    align-items: stretch;
  }

  .ai-filter-group {
    min-width: 100%;
  }
}

.s-label,
.p-label {
  width: 60px;
  min-height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #888;
  font-size: 0.62rem;
  line-height: 1.1;
  text-align: center;
  padding: 0.15rem;
}

.s-label {
  width: 30px;
  height: 60px;
  margin-right: 10px;
}

.labels-row {
  margin-top: 10px;
}
</style>
